// stores/hashtagStore.js
import { writable, derived } from "svelte/store";

// Create the base store
const createHashtagStore = () => {
  // Structure: { [hashtag]: { count: number, blockIds: Set<string> } }
  const { subscribe, update, set } = writable({});

  return {
    subscribe,

    // Add a hashtag with its associated block ID
    addHashtag: (hashtag, blockId) => {
      update((hashtags) => {
        if (!hashtags[hashtag]) {
          hashtags[hashtag] = { count: 0, blockIds: new Set() };
        }

        hashtags[hashtag].blockIds.add(blockId);
        hashtags[hashtag].count = hashtags[hashtag].blockIds.size;

        return hashtags;
      });
    },

    // Remove a hashtag from a specific block
    removeHashtag: (hashtag, blockId) => {
      update((hashtags) => {
        if (hashtags[hashtag]) {
          hashtags[hashtag].blockIds.delete(blockId);
          hashtags[hashtag].count = hashtags[hashtag].blockIds.size;

          // If no blocks use this hashtag anymore, remove it
          if (hashtags[hashtag].count === 0) {
            delete hashtags[hashtag];
          }
        }

        return hashtags;
      });
    },

    // Update hashtags for a block (replaces old hashtags with new ones)
    updateBlockHashtags: (blockId, newHashtags) => {
      update((hashtags) => {
        // First, find all hashtags that contain this blockId
        const hashtagsToUpdate = Object.entries(hashtags)
          .filter(([, data]) => data.blockIds.has(blockId))
          .map(([tag]) => tag);

        // Remove this blockId from all hashtags
        hashtagsToUpdate.forEach((tag) => {
          hashtags[tag].blockIds.delete(blockId);
          hashtags[tag].count = hashtags[tag].blockIds.size;

          if (hashtags[tag].count === 0) {
            delete hashtags[tag];
          }
        });

        // Now add all new hashtags
        newHashtags.forEach((tag) => {
          if (!hashtags[tag]) {
            hashtags[tag] = { count: 0, blockIds: new Set() };
          }

          hashtags[tag].blockIds.add(blockId);
          hashtags[tag].count = hashtags[tag].blockIds.size;
        });

        return hashtags;
      });
    },

    // Clear all hashtags
    clear: () => set({}),

    // Delete a block and remove its associations with all hashtags
    deleteBlock: (blockId) => {
      update((hashtags) => {
        Object.entries(hashtags).forEach(([tag, data]) => {
          if (data.blockIds.has(blockId)) {
            data.blockIds.delete(blockId);
            data.count = data.blockIds.size;

            if (data.count === 0) {
              delete hashtags[tag];
            }
          }
        });

        return hashtags;
      });
    },
  };
};

// Create and export the store
export const hashtagStore = createHashtagStore();

// Derived store that gives an array of hashtags sorted by usage count
export const sortedHashtags = derived(hashtagStore, ($hashtags) => {
  return Object.entries($hashtags)
    .map(([tag, data]) => ({
      tag,
      count: data.count,
      blockIds: [...data.blockIds],
    }))
    .sort((a, b) => b.count - a.count);
});

// Derived store for hashtag search/filtering
export const searchHashtags = (searchTerm) => {
  return derived(hashtagStore, ($hashtags) => {
    const term = searchTerm.toLowerCase();
    return Object.keys($hashtags)
      .filter((tag) => tag.toLowerCase().includes(term))
      .map((tag) => ({
        tag,
        count: $hashtags[tag].count,
        blockIds: [...$hashtags[tag].blockIds],
      }))
      .sort((a, b) => b.count - a.count);
  });
};
